import { useCallback, useMemo, useState } from 'react';
import type { IDocumentAnnotation } from '@apis/documentAnnotations';

/**
 * Which annotations are painted. Every one is checked on arrival — that is the
 * drawing as it stands — and unchecking one takes its author's marks out of the
 * picture and nothing else (docs/specs/2026-09-23-document-markup-layers-design.md).
 *
 * Hidden ids are held rather than visible ones, so an annotation that appears
 * while the page is open (someone else drew) shows up checked.
 */
export function useVisibleAnnotations(annotations: readonly IDocumentAnnotation[]) {
  const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const visible = useMemo(
    () => annotations.filter((a) => !hiddenIds.has(a.id)),
    [annotations, hiddenIds],
  );

  const isVisible = useCallback((id: string) => !hiddenIds.has(id), [hiddenIds]);

  return { visible, isVisible, toggle };
}
